// ─────────────────────────────────────────────
// KLOTHIQ — Add Variant Modal Component
// Allows adding a new size/color variant to an existing product
// ─────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input } from '@/components/ui/FormControls';
import { useState, useEffect, useRef } from 'react';
import { useCreateVariant, useProduct } from '@/hooks/useProducts';
import { useJerseyIdentifiers } from '@/hooks/useJerseyIdentifiers';
import JerseySkuReview from './JerseySkuReview';
import JerseyDetailsFields from './JerseyDetailsFields';
import { useJerseyEntry } from '@/hooks/useJerseyEntry';
import {
  assertJerseyReview,
  jerseySkuPreview,
  type JerseySkuReview as Review,
  jerseyEntrySku,
  jerseyEntryIssues,
  type JerseyDetailsV2,
} from '@klothiq/domain';
import { toast } from 'sonner';
import { Loader2, X, PlusCircle } from 'lucide-react';
import { useOperationIntent } from '@/hooks/useOperationIntent';
import { useQueryClient } from '@tanstack/react-query';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';

export interface AddVariantModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  productCode: string;
  productName: string;
  defaultCost?: number;
  defaultPrice?: number;
}

export default function AddVariantModal({
  isOpen,
  onClose,
  productId,
  productCode,
  productName,
  defaultCost = 0,
  defaultPrice = 0,
}: AddVariantModalProps) {
  const createVariant = useCreateVariant(productId);
  const client = useQueryClient();
  const recovery = useOperationIntent(`add-variant:${productId}`);
  const product = useProduct(productId);
  const jersey = useJerseyIdentifiers(product.data?.product_type_id || undefined, productId);
  const entry = useJerseyEntry(product.data?.product_type_id || undefined, productId);
  const entryEnabled = entry.data?.enabled === true;
  const [details, setDetails] = useState<JerseyDetailsV2>({});
  const initialized = useRef(false);
  const saving = useRef(false);
  useEffect(() => {
    if (isOpen && entry.data && !initialized.current) {
      setDetails(entry.data.current?.details || {});
      initialized.current = true;
    }
  }, [isOpen, entry.data]);
  const [review, setReview] = useState<Review | null>(null);
  const jerseyEnabled = !entryEnabled && jersey.data?.enabled === true;

  const [size, setSize] = useState('');
  const [color, setColor] = useState('');
  const [sku, setSku] = useState('');
  const [cost, setCost] = useState(defaultCost > 0 ? defaultCost.toString() : '0');
  const [price, setPrice] = useState(defaultPrice > 0 ? defaultPrice.toString() : '0');
  const [initialStock, setInitialStock] = useState('0');
  const [lowStock, setLowStock] = useState('5');
  const [autoSku, setAutoSku] = useState(true);
  let generatedSku = '';
  if (entryEnabled) {
    try {
      generatedSku = jerseyEntrySku(details, productCode, color, size, entry.data!);
    } catch {
      /* Supplied optional values are validated before save. */
    }
  }
  if (!entryEnabled && jersey.data?.config && (review || jersey.data.review)) {
    try {
      generatedSku = jerseySkuPreview(
        jersey.data.config,
        (review || jersey.data.review)!.details,
        productCode,
        color,
        size,
      );
    } catch {
      /* A precise validation message is shown on submit. */
    }
  }

  // Auto-generate SKU when size or color changes if autoSku is enabled
  useEffect(() => {
    if (!entryEnabled && autoSku && productCode && size) {
      const cleanSize = size.trim().toUpperCase().replace(/\s+/g, '-');
      const cleanColor = color.trim() ? `-${color.trim().toUpperCase().replace(/\s+/g, '-')}` : '';
      setSku(`${productCode.toUpperCase()}-${cleanSize}${cleanColor}`);
    }
  }, [size, color, productCode, autoSku, entryEnabled]);

  // Update cost and price defaults if props change
  useEffect(() => {
    if (isOpen) {
      if (defaultCost > 0 && cost === '0') setCost(defaultCost.toString());
      if (defaultPrice > 0 && price === '0') setPrice(defaultPrice.toString());
    }
  }, [isOpen, defaultCost, defaultPrice]);

  if (!isOpen) return null;

  const resetForm = () => {
    setSize('');
    setColor('');
    setSku('');
    setCost(defaultCost > 0 ? defaultCost.toString() : '0');
    setPrice(defaultPrice > 0 ? defaultPrice.toString() : '0');
    setInitialStock('0');
    setLowStock('5');
    setAutoSku(true);
    setReview(null);
    setDetails({});
    initialized.current = false;
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving.current || createVariant.isPending) return;
    if (entry.isFetching || entry.error || (entry.data?.entry_config?.enabled && !entryEnabled)) {
      toast.error('Optional jersey entry is unavailable. Retry its settings before saving.');
      return;
    }

    if (!size.trim()) {
      toast.error('Size is required');
      return;
    }

    if (!jerseyEnabled && !(entryEnabled && autoSku) && !sku.trim()) {
      toast.error('SKU is required');
      return;
    }
    if (jersey.isFetching || jersey.error) {
      toast.error('Identifier settings are unavailable. Retry before saving.');
      return;
    }
    if (jerseyEnabled) {
      try {
        if (
          !review?.confirmed ||
          review.configuration_version_id !== jersey.data?.configuration_version_id
        )
          throw new Error('Review and confirm the jersey SKU details.');
        assertJerseyReview(jersey.data!, review);
        jerseySkuPreview(jersey.data!.config!, review.details, productCode, color, size);
      } catch (e) {
        toast.error((e as Error).message);
        return;
      }
    }
    if (entryEnabled && Object.keys(jerseyEntryIssues(details)).length) {
      toast.error(Object.values(jerseyEntryIssues(details))[0]);
      return;
    }

    const parsedCost = parseFloat(cost) || 0;
    const parsedPrice = parseFloat(price) || 0;
    const parsedStock = initialStock.trim() ? Number(initialStock) : 0;
    const parsedLowStock = lowStock.trim() === '' ? 5 : Number(lowStock);

    saving.current = true;
    try {
      await createVariant.mutateAsync({
        product_id: productId,
        size: size.trim(),
        color: color.trim() || undefined,
        sku:
          jerseyEnabled || (entryEnabled && autoSku)
            ? ''
            : entryEnabled
              ? sku.trim()
              : sku.trim().toUpperCase(),
        default_cost: parsedCost,
        default_price: parsedPrice,
        initial_stock: parsedStock,
        low_stock_level: parsedLowStock,
        jersey_review: jerseyEnabled ? review! : undefined,
        expected_updated_at: product.data?.updated_at,
        ...(entryEnabled
          ? {
              jersey_entry: {
                details,
                sku_mode: autoSku ? ('auto' as const) : ('manual' as const),
              },
            }
          : {}),
      });

      toast.success(`Variant "${size.trim()}" added to ${productName}`);
      handleClose();
    } catch (err: unknown) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to create variant');
    } finally {
      saving.current = false;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      data-field-guide-scope
    >
      <div className="flex max-h-[90dvh] w-full max-w-md flex-col overflow-hidden rounded-lg border border-border bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <PlusCircle className="h-5 w-5 text-primary" />
            <div className="flex items-start justify-between gap-3 pr-8">
              <h3 className="text-base font-semibold text-foreground">Add Size / Variant</h3>
              <UserGuide compact />
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mb-4 text-xs text-muted-foreground">
          Adding a new size variant for{' '}
          <span className="font-semibold text-foreground">{productName}</span> ({productCode})
        </p>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <fieldset
            disabled={createVariant.isPending}
            className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1"
          >
            {entryEnabled && entry.data && (
              <JerseyDetailsFields value={details} context={entry.data} onChange={setDetails} />
            )}
            {jerseyEnabled && jersey.data && (
              <JerseySkuReview
                context={jersey.data}
                value={review || jersey.data.review}
                onChange={setReview}
              />
            )}
            {jersey.error && (
              <p role="alert" className="text-sm text-destructive">
                {jersey.error.message}{' '}
                <button
                  type="button"
                  className="min-h-11 underline"
                  onClick={() => void jersey.refetch()}
                >
                  Retry
                </button>
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label htmlFor="variant-add-size" className="text-xs font-medium text-foreground">
                  Size <span className="text-destructive">*</span>
                </label>
                <Input
                  id="variant-add-size"
                  type="text"
                  value={size}
                  onChange={(e) => {
                    setSize(e.target.value);
                  }}
                  placeholder="e.g. S / M / L / XL"
                  required
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Size *"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="variant-add-color" className="text-xs font-medium text-foreground">
                  Color / Option
                </label>
                <Input
                  id="variant-add-color"
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="e.g. Pink / White"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Color / Option"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="variant-add-sku" className="text-xs font-medium text-foreground">
                  SKU <span className="text-destructive">*</span>
                </label>
                {!jerseyEnabled && (
                  <label className="flex items-center gap-1 text-[11px] text-muted-foreground cursor-pointer select-none">
                    <Input
                      type="checkbox"
                      checked={autoSku}
                      onChange={(e) => setAutoSku(e.target.checked)}
                      className="rounded border-input text-primary focus:ring-primary h-3 w-3"
                      helpLabel="Auto-generate"
                    />
                    Auto-generate
                  </label>
                )}
              </div>
              <Input
                id="variant-add-sku"
                type="text"
                value={jerseyEnabled || (entryEnabled && autoSku) ? generatedSku : sku}
                readOnly={jerseyEnabled}
                onChange={(e) => {
                  setAutoSku(false);
                  setSku(e.target.value);
                }}
                placeholder="e.g. KIQ-001-XXL"
                required
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm font-mono uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                helpLabel="SKU"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label htmlFor="variant-add-cost" className="text-xs font-medium text-foreground">
                  Cost (₹) <span className="text-destructive">*</span>
                </label>
                <Input
                  id="variant-add-cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  required
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Cost (₹) *"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="variant-add-price" className="text-xs font-medium text-foreground">
                  Sell Price (₹) <span className="text-destructive">*</span>
                </label>
                <Input
                  id="variant-add-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  required
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Sell Price (₹) *"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label htmlFor="variant-add-stock" className="text-xs font-medium text-foreground">
                  Initial Stock (Qty)
                </label>
                <Input
                  id="variant-add-stock"
                  type="number"
                  min="0"
                  value={initialStock}
                  onChange={(e) => setInitialStock(e.target.value)}
                  placeholder="0"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Initial Stock (Qty)"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="variant-add-low" className="text-xs font-medium text-foreground">
                  Low Stock Limit
                </label>
                <Input
                  id="variant-add-low"
                  type="number"
                  min="0"
                  value={lowStock}
                  onChange={(e) => setLowStock(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Low Stock Limit"
                />
              </div>
            </div>
          </fieldset>
          {createVariant.error && (
            <div role="alert" className="mt-3 space-y-1 text-xs text-destructive">
              <p>{createVariant.error.message}</p>
              <button
                type="button"
                className="min-h-11 underline"
                onClick={() =>
                  void recovery
                    .recover('create')
                    .then(async (result) => {
                      if (!result) {
                        toast.info('No completed save found. Keep the same details when retrying.');
                        return;
                      }
                      await refreshSaleConsumers(client);
                      toast.success('The previous variant save was recovered.');
                      onClose();
                    })
                    .catch((e) => toast.error(e.message))
                }
              >
                Recover previous variant save
              </button>
            </div>
          )}
          <div className="mt-4 flex shrink-0 justify-end gap-2 border-t border-border pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                createVariant.isPending ||
                product.isLoading ||
                jersey.isFetching ||
                !!jersey.error ||
                entry.isFetching ||
                !!entry.error
              }
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {createVariant.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Create Size Variant
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
